/**
 * Zod schemas for auth forms.
 *
 * **Dual validation rule** (per PROJECT_BRIEF §19 + memory:feedback-dual-validation):
 * These run client-side for instant UX feedback. The backend (auth service) ALSO
 * validates with Pydantic — never trust the client.
 */
import { z } from 'zod';

export const emailSchema = z
  .string()
  .trim()
  .min(1, 'Email is required')
  .email('Enter a valid email address');

export const passwordSchema = z
  .string()
  .min(10, 'Use at least 10 characters')
  .max(256, 'That is unusually long — try a passphrase');

export const SignupSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
    confirm: z.string(),
    consent: z.boolean().refine((v) => v === true, {
      message: 'You must agree to the privacy policy and terms',
    }),
  })
  .refine((d) => d.password === d.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  });

export type SignupValues = z.infer<typeof SignupSchema>;

export const LoginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});

export type LoginValues = z.infer<typeof LoginSchema>;

export const ForgotPasswordSchema = z.object({
  email: emailSchema,
});

export type ForgotPasswordValues = z.infer<typeof ForgotPasswordSchema>;
